import { Store, MapPin, Phone, Mail, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useSettings } from "../context/SettingsContext";

export default function Footer() {
  const { settings, categories, footerPages } = useSettings();
  const storeName = settings.store_name || "Victory General Store";
  const [email, setEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubscribing(true);
    try {
      await api.post("/subscribers", { email });
      toast.success("Subscribed! We'll alert you about new stock & offers.");
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="text-white mt-auto" style={{ backgroundColor: "var(--footer-bg)" }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-4 gap-8">
        <div className="md:col-span-1">
          <div className="flex items-center gap-2 mb-4">
            {settings.logo_url ? (
              <img src={settings.logo_url} alt={storeName} className="h-9 w-auto max-w-[150px] object-contain bg-white/10 rounded p-1" />
            ) : (
              <span className="w-9 h-9 rounded-lg bg-[var(--brand-primary)] flex items-center justify-center">
                <Store className="w-5 h-5 text-white" strokeWidth={1.5} />
              </span>
            )}
            <span className="font-display font-bold text-lg">{storeName}</span>
          </div>
          <p className="text-white/60 text-sm leading-relaxed">
            {settings.footer_text ||
              "Your neighbourhood store for everyday essentials. Order easily and confirm on WhatsApp."}
          </p>
          <form onSubmit={subscribe} className="mt-5">
            <p className="text-white/80 text-sm font-medium mb-2">Get new stock &amp; offer alerts</p>
            <div className="flex gap-2">
              <input
                data-testid="subscribe-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white placeholder-white/40 focus:outline-none focus:border-white/50"
              />
              <button
                data-testid="subscribe-submit"
                type="submit"
                disabled={subscribing}
                className="bg-white/15 hover:bg-white/25 text-white rounded-lg px-3 transition-colors disabled:opacity-60"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>

        <div>
          <h4 className="font-display font-semibold mb-4">Shop</h4>
          <ul className="space-y-2 text-white/60 text-sm">
            {categories.map((c) => (
              <li key={c.key}>
                <Link to={`/shop?category=${c.key}`} data-testid={`footer-cat-${c.key}`} className="hover:text-white transition-colors">
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold mb-4">Company</h4>
          <ul className="space-y-2 text-white/60 text-sm">
            {footerPages.map((p) => (
              <li key={p.slug}>
                <Link to={`/page/${p.slug}`} data-testid={`footer-page-${p.slug}`} className="hover:text-white transition-colors">
                  {p.title}
                </Link>
              </li>
            ))}
            {footerPages.length === 0 && <li className="text-white/40">—</li>}
          </ul>
        </div>

        <div>
          <h4 className="font-display font-semibold mb-4">Reach Us</h4>
          <ul className="space-y-3 text-white/60 text-sm">
            {settings.contact_address && (
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.5} /> {settings.contact_address}
              </li>
            )}
            {settings.contact_phone && (
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4" strokeWidth={1.5} /> {settings.contact_phone}
              </li>
            )}
            {settings.contact_email && (
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" strokeWidth={1.5} /> {settings.contact_email}
              </li>
            )}
            {!settings.contact_phone && !settings.contact_email && (
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4" strokeWidth={1.5} /> Order via WhatsApp
              </li>
            )}
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-4 text-center text-white/40 text-xs">
        © {new Date().getFullYear()} {storeName}. All rights reserved.
      </div>
    </footer>
  );
}
